import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { templates } from '@sarnia/shared';
import PublicLayout from '../../components/layout/PublicLayout';
import { Icon } from '../../components/ui/Icon';
import { templateMatchesQuery } from '../../lib/templateSearch';
import './templates.css';

/** The blank template has its own "Start from scratch" card, so it is not repeated in the grid. */
const listable = templates.filter((template) => template.id !== 'blank');

const CLASSIC_FILTER = 'Classic builder';

export function TemplateGalleryPage() {
  const [selectedFilter, setSelectedFilter] = useState<string>('All');
  const [query, setQuery] = useState('');

  const filters = useMemo(() => {
    const categories = Array.from(
      new Set(
        listable
          .filter((template) => template.category)
          .map((template) => template.category as string)
      )
    );
    return ['All', CLASSIC_FILTER, ...categories];
  }, []);

  const visibleTemplates = useMemo(() => {
    const byFilter =
      selectedFilter === 'All'
        ? listable
        : selectedFilter === CLASSIC_FILTER
          ? listable.filter((template) => !template.category)
          : listable.filter((template) => template.category === selectedFilter);
    return byFilter.filter((template) => templateMatchesQuery(template, query));
  }, [selectedFilter, query]);

  return (
    <PublicLayout>
      <div className="tg-gallery">
        <header className="tg-hero">
          <h1>Pick a starting point</h1>
          <p>
            {listable.length + 1} ways to launch a beautiful site. Every template is responsive,
            accessible and ready to make yours.
          </p>
        </header>

        <div className="tg-searchbar">
          <Icon name="search" size={18} />
          <input
            type="search"
            className="tg-search"
            placeholder="Search templates: salon, plumber, wedding, cafe..."
            aria-label="Search templates"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div className="tg-filters" aria-label="Filter templates">
          {filters.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`tg-chip${selectedFilter === filter ? ' tg-chip--selected' : ''}`}
              aria-pressed={selectedFilter === filter}
              onClick={() => setSelectedFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>

        <div className="tg-grid">
          <Link
            to="/dashboard/sites/new?template=blank"
            className="tg-blank-card"
            aria-label="Start from scratch with a blank template"
          >
            <span className="tg-blank-icon" aria-hidden="true">
              <Icon name="file-plus" size={28} />
            </span>
            <span className="tg-blank-body">
              <span className="tg-blank-title">Start from scratch</span>
              <span className="tg-blank-description">
                A blank canvas with a guided tutorial that teaches you the editor as you build.
              </span>
            </span>
          </Link>

          {visibleTemplates.length === 0 ? (
            <p className="tg-empty" role="status">
              No templates match &ldquo;{query}&rdquo;. Try a broader word like food, health or shop, or start from scratch.
            </p>
          ) : null}

          {visibleTemplates.map((template) => {
            return (
              <article key={template.id} className="tg-card">
                <div className="tg-card__preview">
                  {template.previewImage ? (
                    <img
                      src={template.previewImage}
                      alt=""
                      className="tg-card__image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="tg-card__swatch" aria-hidden="true">
                      <span style={{ background: template.site.theme.palette.accent }} />
                      <span style={{ background: template.site.theme.palette.text }} />
                      <span style={{ background: template.site.theme.palette.surface }} />
                    </div>
                  )}
                  {template.category && (
                    <span className="sarnia-badge sarnia-badge--neutral tg-card__category">
                      {template.category}
                    </span>
                  )}
                </div>
                <div className="tg-card__body">
                  <h2>{template.name}</h2>
                  <p>{template.description}</p>
                  <div className="tg-card__actions">
                    <Link to={`/templates/${template.id}`} className="sarnia-btn sarnia-btn--ghost sarnia-btn--sm">
                      Preview
                    </Link>
                    <Link
                      to={`/dashboard/sites/new?template=${template.id}`}
                      className="sarnia-btn sarnia-btn--primary sarnia-btn--sm"
                    >
                      Use template
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </PublicLayout>
  );
}
